import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ArrowRight, Star, Bell, X, ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';
import MetaTags from '@/components/MetaTags';
import CrawlCard from '@/components/CrawlCard';
import axios from 'axios';
import { API } from '@/App';
import { trackCtaClick, trackCityPillClick, trackLayloClick, trackSectionView } from '@/utils/analytics';
import { resolveMediaUrl, isVideoUrl } from '@/utils/media';

const AnimatedCounter = ({ end, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      const duration = 1500;
      const startTime = Date.now();
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const current = Math.floor(easeOutCubic(progress) * end);
        setCount(current);
        if (progress < 1) requestAnimationFrame(animate);
        else setCount(end);
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, end]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

/* SVG Icons for How It Works */
const CityPinIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M32 4C21.5 4 13 12.5 13 23C13 37 32 58 32 58C32 58 51 37 51 23C51 12.5 42.5 4 32 4Z" stroke="#FF2D78" strokeWidth="2.5" fill="none"/>
    <rect x="23" y="26" width="4" height="10" stroke="#FF2D78" strokeWidth="1.5" fill="none"/>
    <rect x="30" y="22" width="4" height="14" stroke="#FF2D78" strokeWidth="1.5" fill="none"/>
    <rect x="37" y="24" width="4" height="12" stroke="#FF2D78" strokeWidth="1.5" fill="none"/>
    <line x1="20" y1="36" x2="44" y2="36" stroke="#FF2D78" strokeWidth="1.5"/>
  </svg>
);

const WristbandIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="8" y="18" width="48" height="28" rx="4" stroke="#00F5FF" strokeWidth="2.5" fill="none"/>
    <line x1="38" y1="18" x2="38" y2="46" stroke="#00F5FF" strokeWidth="1.5" strokeDasharray="3 3"/>
    <circle cx="24" cy="32" r="6" stroke="#00F5FF" strokeWidth="1.5" fill="none"/>
    <path d="M21 32L23 34L27 30" stroke="#00F5FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <text x="44" y="30" fill="#00F5FF" fontSize="7" fontFamily="DM Mono" textAnchor="middle">VIP</text>
    <text x="44" y="38" fill="#00F5FF" fontSize="5" fontFamily="DM Mono" textAnchor="middle">PASS</text>
  </svg>
);

const PlatformIcon = ({ platform }) => {
  switch (platform) {
    case 'x':
      return <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
    case 'instagram':
      return <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/></svg>;
    case 'tiktok':
      return <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78 2.92 2.92 0 0 1 .88.13V9.03a6.35 6.35 0 0 0-.88-.07 6.34 6.34 0 0 0 0 12.68 6.34 6.34 0 0 0 6.33-6.34V8.56a8.27 8.27 0 0 0 3.77.92V6.69z"/></svg>;
    case 'facebook':
      return <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
    default:
      return null;
  }
};

const platformMeta = {
  x: { label: 'X', bg: '#000000', text: '#FFFFFF', handleColor: '#71767B' },
  instagram: { label: 'Instagram', bg: '#FFFFFF', text: '#262626', handleColor: '#8E8E8E' },
  tiktok: { label: 'TikTok', bg: '#000000', text: '#FFFFFF', handleColor: '#A0A0A0' },
  facebook: { label: 'Facebook', bg: '#FFFFFF', text: '#1C1E21', handleColor: '#65676B' },
};

const SocialCard = ({ post, index }) => {
  const meta = platformMeta[post.platform];
  const isDark = post.platform === 'x' || post.platform === 'tiktok';

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      className="rounded-xl overflow-hidden shadow-md"
      style={{ backgroundColor: meta.bg, border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)' }}
      data-testid={`social-card-${index}`}
    >
      <div className="p-4">
        {/* Header — platform icon + handle + time */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: post.accentColor }}>
            <span className="text-[#080010]"><PlatformIcon platform={post.platform} /></span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-subheading text-[13px] font-semibold truncate" style={{ color: meta.text }}>
              {post.platform === 'facebook' ? post.handle : post.name}
            </p>
            <p className="font-mono text-[10px]" style={{ color: meta.handleColor }}>
              {post.platform === 'facebook' ? post.time + ' ago' : post.handle + ' · ' + post.time}
            </p>
          </div>
          <div className="flex-shrink-0" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)' }}>
            <PlatformIcon platform={post.platform} />
          </div>
        </div>

        {/* Post body */}
        <p className="font-body text-[14px] leading-relaxed" style={{ color: isDark ? 'rgba(255,255,255,0.9)' : meta.text }}>
          {post.quote}
        </p>

        {/* City tag */}
        <div className="mt-3 flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[1px] px-2 py-0.5 rounded-full" style={{ backgroundColor: post.accentColor + '20', color: post.accentColor }}>
            {post.city}
          </span>
          <span className="font-mono text-[10px]" style={{ color: meta.handleColor }}>
            #CrawlBabyCrawl
          </span>
        </div>

        {/* Engagement bar */}
        <div className="flex items-center gap-4 mt-3 pt-3" style={{ borderTop: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)' }}>
          <span className="font-mono text-[10px]" style={{ color: meta.handleColor }}>
            {[147, 89, 234, 112, 67, 198][index % 6]} likes
          </span>
          <span className="font-mono text-[10px]" style={{ color: meta.handleColor }}>
            {[23, 8, 31, 14, 19, 11][index % 6]} replies
          </span>
          <span className="font-mono text-[10px]" style={{ color: meta.handleColor }}>
            {[42, 17, 56, 28, 33, 44][index % 6]} reposts
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const PartyIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 48L24 24H40L44 48H20Z" stroke="#B8FF2E" strokeWidth="2.5" fill="none" strokeLinejoin="round"/>
    <path d="M24 24C24 24 28 20 32 20C36 20 40 24 40 24" stroke="#B8FF2E" strokeWidth="1.5"/>
    <ellipse cx="32" cy="24" rx="10" ry="2" stroke="#B8FF2E" strokeWidth="1.5" fill="none"/>
    <path d="M14 14L18 20" stroke="#B8FF2E" strokeWidth="2" strokeLinecap="round"/>
    <path d="M50 14L46 20" stroke="#B8FF2E" strokeWidth="2" strokeLinecap="round"/>
    <path d="M32 8V14" stroke="#B8FF2E" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="12" cy="12" r="2" fill="#FF2D78"/>
    <circle cx="52" cy="12" r="2" fill="#B8FF2E"/>
    <circle cx="32" cy="6" r="2" fill="#00F5FF"/>
  </svg>
);

const HomePage = () => {
  const [crawls, setCrawls] = useState([]);
  const [stats, setStats] = useState({ total_crawls: 200, total_crawlers: 35000, cities_count: 30 });
  const [cityPills, setCityPills] = useState(['Chicago', 'Austin', 'NYC', 'Miami', 'Nashville', 'Denver']);
  const [howItWorks, setHowItWorks] = useState([
    { step: '01', title: 'Pick Your City', desc: 'Browse crawls in 30+ cities nationwide' },
    { step: '02', title: 'Buy Your Wristband', desc: 'Secure your spot with General or VIP tickets' },
    { step: '03', title: 'Show Up & Crawl', desc: 'Meet the crew, grab your wristband, party' },
  ]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [showSticky, setShowSticky] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const heroVideoRef = useRef(null);

  // Try to autoplay with audio; if browser blocks it, fall back to muted
  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return;
    video.muted = false;
    const playPromise = video.play();
    if (playPromise) {
      playPromise.catch(() => {
        video.muted = true;
        setIsMuted(true);
        video.play();
      });
    }
  }, []);
  const [showDropModal, setShowDropModal] = useState(false);
  const [dropSubmitted, setDropSubmitted] = useState(false);
  const [dropPhone, setDropPhone] = useState('');
  const [dropLoading, setDropLoading] = useState(false);
  const [notifyPhone, setNotifyPhone] = useState('');
  const [notifySubmitted, setNotifySubmitted] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    fetchCrawls();
    fetchStats();
    fetchSiteConfig();
    fetchGallery();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowSticky(window.scrollY > window.innerHeight);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchCrawls = async () => {
    try {
      const response = await axios.get(`${API}/crawls?is_featured=true`);
      setCrawls(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching crawls:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSiteConfig = async () => {
    try {
      const response = await axios.get(`${API}/site-config`);
      if (response.data.city_pills) setCityPills(response.data.city_pills);
      if (response.data.how_it_works) setHowItWorks(response.data.how_it_works);
    } catch (error) {
      console.error('Error fetching site config:', error);
    }
  };

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      if (response.data.length > 0) {
        setGalleryImages(response.data.map(g => ({ src: g.image_url, height: g.height, tint: g.tint })));
      } else {
        setGalleryImages([
          { src: 'https://images.unsplash.com/photo-1758272133795-3a21773ebc5e?w=600', height: 200, tint: false },
          { src: 'https://images.unsplash.com/photo-1562066197-ee227ba3f502?w=600', height: 280, tint: true },
          { src: 'https://images.pexels.com/photos/8907375/pexels-photo-8907375.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', height: 200, tint: false },
          { src: 'https://images.unsplash.com/photo-1758244241016-20a95d099de1?w=600', height: 280, tint: true },
          { src: 'https://images.unsplash.com/photo-1766650577234-683e2842c4fe?w=600', height: 200, tint: false },
          { src: 'https://images.unsplash.com/photo-1504196606672-aef5c9cefc92?w=600', height: 280, tint: true },
        ]);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
    }
  };

  const [socialIndex, setSocialIndex] = useState(0);

  const socialProof = [
    { platform: 'x', handle: '@sarahmiami23', name: 'Sarah', quote: 'just did the CBC Miami crawl and holy shit... best night out I\'ve had in years. the drink specials were insane and our guide knew all the best spots. 10/10 no notes', city: 'MIAMI', accentColor: '#FF2D78', time: '8h' },
    { platform: 'instagram', handle: '@jake_atx', name: 'Jake', quote: 'Birthday crawl on 6th street was ELITE. VIP was totally worth it — skip the lines, extra shots, great vibes all night. CBC knows how to throw it', city: 'AUSTIN', accentColor: '#B8FF2E', time: '2d' },
    { platform: 'tiktok', handle: '@amandanyc_', name: 'Amanda', quote: 'the speakeasy crawl was UNREAL. places I never would have found on my own. if you\'re in NYC this is the move. the hidden bar with the teacup cocktails?? DEAD', city: 'NYC', accentColor: '#00F5FF', time: '14h' },
    { platform: 'facebook', handle: 'Marcus Johnson', name: 'Marcus', quote: 'Brought the whole crew to Nashville and it was the best group night we\'ve ever done. Tootsie\'s, Robert\'s, Kid Rock\'s rooftop... legendary. Already booking Chicago next', city: 'NASHVILLE', accentColor: '#FFB347', time: '3d' },
    { platform: 'x', handle: '@chitown_mike', name: 'Mike', quote: 'CBC Chicago was a whole different level. Wicker Park dive bars into cocktail lounges, ping pong at Happy Village... they mapped out the perfect route', city: 'CHICAGO', accentColor: '#A855F7', time: '1d' },
    { platform: 'instagram', handle: '@dani_sandiego', name: 'Dani', quote: 'rooftop views at Altitude, dueling pianos at Shout House, speakeasy vibes at Prohibition... the Gaslamp crawl goes crazy. obsessed', city: 'SAN DIEGO', accentColor: '#00F5FF', time: '5h' },
  ];

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setSocialIndex((prev) => (prev + 1) % socialProof.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [socialProof.length]);

  const stepIcons = [<CityPinIcon />, <WristbandIcon />, <PartyIcon />];

  const handleDropSubmit = async (e) => {
    e.preventDefault();
    if (!dropPhone.trim()) return;
    setDropLoading(true);
    try {
      await axios.post(`${API}/drop-subscribers`, { phone: dropPhone });
      setDropSubmitted(true);
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.detail === 'Already on the list') {
        setDropSubmitted(true);
      }
    } finally {
      setDropLoading(false);
    }
  };

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    if (!notifyPhone.trim()) return;
    setNotifyLoading(true);
    try {
      await axios.post(`${API}/subscribers`, { phone: notifyPhone });
      setNotifySubmitted(true);
    } catch (err) {
      if (err.response?.status === 400) {
        setNotifySubmitted(true);
      }
    } finally {
      setNotifyLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-deep-black" data-testid="home-page">
      <MetaTags
        title="Crawl Baby Crawl — Bar to Bar. Block to Block."
        description="The bar crawl experience America has been waiting for. 30+ cities nationwide. Find your crawl and get your wristband."
        path="/"
      />
      {/* HERO — Full Viewport Video Background */}
      <section className="relative h-screen h-[100dvh] w-full overflow-hidden" data-testid="hero-section">
        <video
          ref={heroVideoRef}
          autoPlay
          playsInline
          poster="/cbc-logo.png"
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ minWidth: '100%', minHeight: '100%' }}
          data-testid="hero-logo-video"
          onEnded={(e) => e.target.pause()}
        >
          <source src="https://customer-assets.emergentagent.com/job_8d9bacaf-3cbd-4601-a4fa-6b92074da917/artifacts/f6jpidzm_b6403a2e24997469accfadacc6f90cb677bf2299a991ef0d52d4adf5a305e8a9.MP4" type="video/mp4" />
        </video>

        {/* Tap to unmute */}
        <button
          onClick={() => {
            if (heroVideoRef.current) {
              heroVideoRef.current.muted = !heroVideoRef.current.muted;
              setIsMuted(heroVideoRef.current.muted);
              if (!heroVideoRef.current.muted && heroVideoRef.current.paused) {
                heroVideoRef.current.play();
              }
            }
          }}
          data-testid="hero-mute-toggle"
          className="absolute top-20 right-4 z-20 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/20 transition-all hover:bg-black/70"
          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
        >
          {isMuted ? <VolumeX size={18} className="text-white" /> : <Volume2 size={18} className="text-white" />}
        </button>

        <div className="absolute inset-0 bg-gradient-to-t from-[#080010] via-[#080010]/40 to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 z-10 px-4 pb-10 md:pb-16">
          <div className="max-w-4xl mx-auto text-center">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="font-subheading text-white text-[13px] md:text-lg uppercase tracking-[2px] md:tracking-[3px] leading-relaxed max-w-[600px] mx-auto mb-6 md:mb-8"
              data-testid="hero-subheadline"
            >
              THE BAR CRAWL EXPERIENCE AMERICA HAS BEEN WAITING FOR
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
            >
              <Link to="/crawls" className="w-full sm:w-auto">
                <button data-testid="hero-cta-btn" className="hero-cta-primary" onClick={() => trackCtaClick('find_a_crawl', 'hero')}>
                  FIND A CRAWL
                </button>
              </Link>
              <a href="#how-it-works" className="w-full sm:w-auto">
                <button className="hero-cta-secondary" data-testid="hero-how-it-works-btn">
                  HOW IT WORKS
                </button>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.45 }}
              className="flex justify-center mb-8 px-4 sm:px-0"
            >
              <button
                onClick={() => { setShowDropModal(true); setDropSubmitted(false); setDropPhone(''); }}
                className="w-full sm:w-auto font-display text-[17px] tracking-[2px] uppercase px-10 py-4 min-h-[48px] transition-all duration-200 hover:brightness-90 hover:scale-[1.02]"
                style={{ backgroundColor: '#B8FF2E', color: '#080010', borderRadius: '9999px' }}
                data-testid="hero-get-the-drop-btn"
              >
                GET THE DROP
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex flex-wrap items-center justify-center gap-3"
            >
              {cityPills.map((city) => (
                <Link key={city} to={`/crawls?city=${city}`}>
                  <button className="city-pill" onClick={() => trackCityPillClick(city)}>{city}</button>
                </Link>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ABOUT HYPE SECTION — Electric lime background */}
      <section className="bg-[#B8FF2E] py-20 md:py-28 px-4 border-b-[5px] border-[#080010]" data-testid="about-hype-section">
        <div className="max-w-[1200px] mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-display text-[48px] md:text-[72px] lg:text-[88px] text-[#080010] uppercase leading-[0.95] tracking-wide"
          >
            WE SHOW UP.<br />WE TURN UP.<br />WE DO IT AGAIN.
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mt-16 md:mt-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <p className="font-display text-[64px] md:text-[80px] leading-none text-hot-pink">
                25+
              </p>
              <p className="font-display text-[28px] md:text-[32px] text-hot-pink uppercase mt-1">CITIES</p>
              <p className="font-subheading text-[12px] text-[#080010]/50 uppercase tracking-[3px] mt-2">AND GROWING</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <p className="font-display text-[40px] md:text-[48px] leading-none text-[#080010]">
                THOUSANDS
              </p>
              <p className="font-display text-[28px] md:text-[32px] text-[#080010] uppercase mt-1">OF CRAWLERS</p>
              <p className="font-subheading text-[12px] text-[#080010]/50 uppercase tracking-[3px] mt-2">NATIONWIDE</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <p className="font-display text-[40px] md:text-[52px] leading-none text-[#1E90FF]">
                DAY & NIGHT
              </p>
              <p className="font-display text-[28px] md:text-[32px] text-[#1E90FF] uppercase mt-1">WE RUN BOTH</p>
              <p className="font-subheading text-[12px] text-[#080010]/50 uppercase tracking-[3px] mt-2">ALL DAY ENERGY</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 5px dark top border, off-white bg */}
      <section id="how-it-works" data-testid="how-it-works-section">
        <div className="h-[5px] bg-[#080010] w-full" />
        <div className="bg-[#F5F0EE] py-14 md:py-32 px-4 relative overflow-hidden" style={{
          backgroundImage: `
            linear-gradient(45deg, rgba(255,45,120,0.03) 25%, transparent 25%),
            linear-gradient(-45deg, rgba(255,45,120,0.03) 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, rgba(255,45,120,0.03) 75%),
            linear-gradient(-45deg, transparent 75%, rgba(255,45,120,0.03) 75%)
          `,
          backgroundSize: '40px 40px',
          backgroundPosition: '0 0, 0 20px, 20px -20px, -20px 0px',
        }}>
          {/* Halftone dot pattern — bottom-right */}
          <div className="absolute bottom-0 right-0 w-[300px] h-[300px] pointer-events-none" aria-hidden="true" style={{
            backgroundImage: 'radial-gradient(circle, rgba(255,45,120,0.15) 2px, transparent 2px)',
            backgroundSize: '16px 16px',
            maskImage: 'radial-gradient(ellipse at bottom right, black 0%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse at bottom right, black 0%, transparent 70%)',
          }} />
          {/* Lime halftone dot cluster — top-left */}
          <div className="absolute top-0 left-0 w-[250px] h-[250px] pointer-events-none" aria-hidden="true" style={{
            backgroundImage: 'radial-gradient(circle, rgba(184,255,46,0.20) 2.5px, transparent 2.5px)',
            backgroundSize: '14px 14px',
            maskImage: 'radial-gradient(ellipse at top left, black 0%, transparent 65%)',
            WebkitMaskImage: 'radial-gradient(ellipse at top left, black 0%, transparent 65%)',
          }} />
          <div className="max-w-[1400px] mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-10 md:mb-20"
            >
              <span className="font-mono text-hot-pink uppercase text-xs tracking-[3px]">Simple Process</span>
              <h2 className="font-display text-[42px] md:text-[72px] text-deep-black uppercase mt-2 leading-none">
                How It Works
              </h2>
            </motion.div>

            {/* Mobile compact cards */}
            <div className="md:hidden space-y-3">
              {howItWorks.map((item, index) => {
                const stepColors = ['#FF2D78', '#00F5FF', '#B8FF2E'];
                const stepColor = stepColors[index];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <div className="flex items-center gap-4 bg-white rounded-lg px-5 py-4 shadow-sm" style={{ borderLeft: `4px solid ${stepColor}` }}>
                      <span className="font-display text-[38px] leading-none flex-shrink-0" style={{ color: stepColor }}>
                        {item.step}
                      </span>
                      <div>
                        <h3 className="font-subheading text-[15px] font-semibold text-deep-black uppercase tracking-wide">
                          {item.title}
                        </h3>
                        <p className="font-body text-[13px] text-[#555] mt-0.5 leading-snug">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Desktop full cards */}
            <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-6 relative">
              {/* Dashed connector lines — alternating pink and lime */}
              <div className="hidden md:block absolute top-[80px] left-[22%] w-[28%] border-t-2 border-dashed" style={{ borderColor: 'rgba(255,45,120,0.4)' }} />
              <div className="hidden md:block absolute top-[80px] right-[22%] w-[28%] border-t-2 border-dashed" style={{ borderColor: 'rgba(184,255,46,0.4)' }} />

              {howItWorks.map((item, index) => {
                const stepColors = ['#FF2D78', '#00F5FF', '#B8FF2E'];
                const stepColor = stepColors[index] || '#FF2D78';
                const stepNums = ['01', '02', '03'];
                return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  className="hiw-step-card text-center group"
                  style={{ borderTop: `4px solid ${stepColor}` }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = stepColor}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.borderTopColor = stepColor; }}
                >
                  {/* Ghost watermark of step number */}
                  <span className="step-ghost">{stepNums[index]}</span>
                  <div className="relative z-10">
                    <div className="flex justify-center mb-4">
                      {stepIcons[index]}
                    </div>
                    <span className="font-display text-[64px] md:text-[96px] leading-none block" style={{ color: stepColor }}>
                      {item.step}
                    </span>
                    <h3 className="font-subheading text-[22px] font-semibold text-deep-black uppercase mt-4 tracking-wide">
                      {item.title}
                    </h3>
                    <p className="font-body text-[15px] text-[#555] mt-2 leading-relaxed max-w-[280px] mx-auto">
                      {item.desc}
                    </p>
                  </div>
                </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED CRAWLS — Hot pink background */}
      <section className="bg-[#FF2D78] py-20 px-4" data-testid="featured-crawls-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span className="font-mono text-white/70 uppercase text-xs tracking-[3px]">Don't Miss Out</span>
              <h2 className="font-display text-[56px] md:text-[72px] text-white uppercase mt-2 leading-none">
                Featured Crawls
              </h2>
            </div>
            <Link to="/crawls" className="mt-4 md:mt-0">
              <span className="font-body text-white/70 hover:text-white transition-colors inline-flex items-center gap-2">
                See all crawls <ArrowRight size={16} />
              </span>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {crawls.map((crawl, index) => (
              <motion.div
                key={crawl.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <CrawlCard crawl={crawl} showTicketButton />
              </motion.div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-12">
            <Link to="/crawls">
              <button
                className="font-display text-[17px] tracking-[2px] uppercase px-10 py-4 min-h-[48px] rounded-full bg-[#00F5FF] text-[#080010] hover:brightness-90 transition-all duration-200"
                data-testid="featured-see-all-crawls-btn"
              >
                SEE ALL CRAWLS
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Reorderable section group — Social Proof moves before Stats on mobile */}
      <div className="flex flex-col">

      {/* STATS BAR — Cyan background, dark numbers */}
      <section className="bg-[#00F5FF] py-16 px-4 relative order-3 md:order-1" data-testid="stats-section">
        <div className="h-[5px] bg-[#080010] w-full absolute top-0 left-0" />
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <p className="stat-dark">
                <AnimatedCounter end={stats.total_crawlers} suffix="+" />
              </p>
              <p className="stat-label-dark mt-3">Crawlers & Counting</p>
            </div>
            <div>
              <p className="stat-dark">
                <AnimatedCounter end={stats.cities_count} suffix="+" />
              </p>
              <p className="stat-label-dark mt-3">Cities Nationwide</p>
            </div>
            <div>
              <p className="stat-dark">
                <AnimatedCounter end={stats.total_crawls} suffix="+" />
              </p>
              <p className="stat-label-dark mt-3">Events Run</p>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY STRIP — Lime top border, dark bg */}
      <section data-testid="gallery-section" className="order-2 md:order-2">
        <div className="h-[5px] bg-[#B8FF2E] w-full" />
        <div className="py-10 overflow-hidden bg-[#080010]">
          <div className="gallery-scroll flex gap-4 overflow-x-auto pb-4 px-4 scrollbar-hide">
            {galleryImages.map((img, index) => (
              <div
                key={index}
                className={`flex-shrink-0 overflow-hidden relative cursor-pointer transition-transform duration-200 hover:scale-[1.02] ${img.tint ? 'gallery-pink-tint' : ''}`}
                style={{ width: img.height === 280 ? '380px' : '280px', height: `${img.height}px` }}
                onClick={() => setLightboxSrc(isVideoUrl(img.src) ? null : resolveMediaUrl(img.src))}
                data-testid={`gallery-image-${index}`}
              >
                {isVideoUrl(img.src) ? (
                  <video src={resolveMediaUrl(img.src)} className="w-full h-full object-cover" muted autoPlay loop playsInline />
                ) : (
                  <img src={resolveMediaUrl(img.src)} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF — 5px dark top border, warm cream bg */}
      <section data-testid="testimonials-section" className="order-1 md:order-3">
        <div className="h-[5px] bg-[#080010] w-full" />
        <div className="bg-[#F5F0EE] py-14 md:py-32 px-4 relative overflow-hidden" style={{
          backgroundImage: `
            linear-gradient(45deg, rgba(255,45,120,0.03) 25%, transparent 25%),
            linear-gradient(-45deg, rgba(255,45,120,0.03) 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, rgba(255,45,120,0.03) 75%),
            linear-gradient(-45deg, transparent 75%, rgba(255,45,120,0.03) 75%)
          `,
          backgroundSize: '40px 40px',
          backgroundPosition: '0 0, 0 20px, 20px -20px, -20px 0px',
        }}>
          {/* Lime halftone dot cluster — top-left */}
          <div className="absolute top-0 left-0 w-[250px] h-[250px] pointer-events-none" aria-hidden="true" style={{
            backgroundImage: 'radial-gradient(circle, rgba(184,255,46,0.20) 2.5px, transparent 2.5px)',
            backgroundSize: '14px 14px',
            maskImage: 'radial-gradient(ellipse at top left, black 0%, transparent 65%)',
            WebkitMaskImage: 'radial-gradient(ellipse at top left, black 0%, transparent 65%)',
          }} />
        <div className="max-w-[1400px] mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8 md:mb-16"
          >
            <span className="font-mono text-hot-pink uppercase text-xs tracking-[3px]">Seen In The Wild</span>
            <h2 className="font-display text-[42px] md:text-[72px] text-deep-black uppercase mt-2 leading-none">
              Social Proof
            </h2>
          </motion.div>

          {/* Desktop grid — show 6 cards in 3-col */}
          <div className="hidden md:grid md:grid-cols-3 gap-6">
            {socialProof.map((post, index) => (
              <SocialCard key={index} post={post} index={index} />
            ))}
          </div>

          {/* Mobile carousel — 1 visible with peek */}
          <div className="md:hidden relative">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${socialIndex * 100}%)` }}
              >
                {socialProof.map((post, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-1">
                    <SocialCard post={post} index={index} />
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation arrows */}
            <button
              onClick={() => setSocialIndex((prev) => (prev - 1 + socialProof.length) % socialProof.length)}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 w-8 h-8 bg-[#080010] text-white rounded-full flex items-center justify-center z-10"
              data-testid="social-prev-btn"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setSocialIndex((prev) => (prev + 1) % socialProof.length)}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 w-8 h-8 bg-[#080010] text-white rounded-full flex items-center justify-center z-10"
              data-testid="social-next-btn"
            >
              <ChevronRight size={16} />
            </button>

            {/* Dot indicators */}
            <div className="flex justify-center gap-2 mt-5">
              {socialProof.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSocialIndex(i)}
                  className="w-2 h-2 rounded-full transition-all duration-300"
                  style={{ backgroundColor: i === socialIndex ? '#FF2D78' : 'rgba(8,0,16,0.15)' }}
                  data-testid={`social-dot-${i}`}
                />
              ))}
            </div>
          </div>
        </div>
        </div>
      </section>

      </div>{/* End reorderable section group */}

      {/* NOTIFY ME SECTION — Two column, lime top border, dark bg */}
      <section data-testid="notify-section">
        <div className="h-[5px] bg-[#B8FF2E] w-full" />
        <div className="bg-[#080010] py-20 md:py-24 px-6 md:px-12">
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
            {/* Left — Headline */}
            <div>
              <h2 className="font-display text-[40px] md:text-[56px] lg:text-[64px] text-white uppercase leading-[0.95]">
                BE FIRST WHEN WE HIT YOUR CITY
              </h2>
              <p className="font-body text-white/50 text-base mt-5 leading-relaxed max-w-[420px]">
                Drop your number. We'll text you the second tickets go live in your city.
              </p>
            </div>

            {/* Right — Phone signup */}
            <div>
              {!notifySubmitted ? (
                <form onSubmit={handleNotifySubmit} className="space-y-4 max-w-[380px]" data-testid="notify-section-form">
                  <input
                    type="tel"
                    value={notifyPhone}
                    onChange={(e) => setNotifyPhone(e.target.value)}
                    placeholder="your phone number"
                    className="w-full px-5 py-4 font-body text-[15px] text-white rounded-md outline-none transition-colors focus:border-[#B8FF2E]"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)' }}
                    data-testid="notify-section-phone"
                    required
                  />
                  <button
                    type="submit"
                    disabled={notifyLoading}
                    className="w-full font-display text-[16px] tracking-[2px] uppercase py-4 transition-all duration-200 hover:brightness-90 hover:scale-[1.02] rounded-md disabled:opacity-50"
                    style={{ backgroundColor: '#B8FF2E', color: '#080010' }}
                    data-testid="notify-section-submit"
                  >
                    {notifyLoading ? 'JOINING...' : 'NOTIFY ME'}
                  </button>
                  <p className="font-body text-[12px] text-white/30 leading-relaxed">
                    We'll only text you when tickets go live in your city. No spam, no BS.
                  </p>
                </form>
              ) : (
                <div className="text-center py-4 max-w-[380px]" data-testid="notify-section-success">
                  <h3 className="font-display text-[36px] md:text-[44px] uppercase leading-none" style={{ color: '#B8FF2E' }}>
                    You're In.
                  </h3>
                  <p className="font-body text-[15px] text-white/80 mt-3">
                    Watch your phone. We'll hit you the second tickets drop in your city.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* STICKY NOTIFY CTA BUTTON */}
      <AnimatePresence>
        {showSticky && !showNotifyModal && (
          <motion.button
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="sticky-notify-btn sticky-slide-in"
            onClick={() => setShowNotifyModal(true)}
            data-testid="sticky-notify-btn"
          >
            <Bell size={18} />
            NOTIFY ME
          </motion.button>
        )}
      </AnimatePresence>

      {/* NOTIFY MODAL */}
      <AnimatePresence>
        {showNotifyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="notify-modal-overlay"
            data-testid="notify-modal"
          >
            {/* Gradient accents */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#FF2D78] via-[#1E90FF] to-[#00F5FF]" />
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#00F5FF] via-[#1E90FF] to-[#FF2D78]" />

            {/* Close button */}
            <button
              className="absolute top-6 right-6 text-warm-white/50 hover:text-white transition-colors z-10"
              onClick={() => setShowNotifyModal(false)}
              data-testid="notify-modal-close"
            >
              <X size={28} />
            </button>

            <div className="text-center px-6 max-w-lg mx-auto">
              {/* Logo */}
              <img src="/cbc-logo.png" alt="CBC" className="w-[140px] h-auto mx-auto mb-10" />

              <h2 className="font-display text-[40px] md:text-[56px] text-white uppercase leading-none">
                BE FIRST WHEN WE<br />HIT YOUR CITY
              </h2>

              <p className="font-body text-warm-white/60 text-base mt-6 mb-8 max-w-[380px] mx-auto leading-relaxed">
                Drop your number and we'll text you the second tickets go live in your city.
              </p>

              {!notifySubmitted ? (
                <form onSubmit={handleNotifySubmit} className="space-y-4 max-w-[380px] mx-auto" data-testid="notify-modal-form">
                  <input
                    type="tel"
                    value={notifyPhone}
                    onChange={(e) => setNotifyPhone(e.target.value)}
                    placeholder="your phone number"
                    className="w-full px-5 py-4 font-body text-[15px] text-white rounded-md outline-none transition-colors focus:border-[#B8FF2E]"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)' }}
                    data-testid="notify-modal-phone"
                    required
                  />
                  <button
                    type="submit"
                    disabled={notifyLoading}
                    className="w-full font-display text-[16px] tracking-[2px] uppercase py-4 transition-all duration-200 hover:brightness-90 hover:scale-[1.02] rounded-md disabled:opacity-50"
                    style={{ backgroundColor: '#B8FF2E', color: '#080010' }}
                    data-testid="notify-modal-submit"
                  >
                    {notifyLoading ? 'JOINING...' : 'NOTIFY ME'}
                  </button>
                  <p className="font-body text-[12px] text-center text-white/30 leading-relaxed">
                    No spam. No BS. Just a text when it matters.
                  </p>
                </form>
              ) : (
                <div className="text-center py-4" data-testid="notify-modal-success">
                  <h3 className="font-display text-[44px] md:text-[52px] uppercase leading-none" style={{ color: '#B8FF2E' }}>
                    You're In.
                  </h3>
                  <p className="font-body text-[15px] text-white/80 mt-3">
                    Watch your phone. We'll hit you the second tickets drop in your city.
                  </p>
                  <button
                    onClick={() => setShowNotifyModal(false)}
                    className="mt-6 font-display text-[16px] tracking-[2px] uppercase px-8 py-3 transition-all duration-200 hover:brightness-90 rounded-md"
                    style={{ backgroundColor: '#B8FF2E', color: '#080010' }}
                    data-testid="notify-modal-done"
                  >
                    GOT IT
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* GALLERY LIGHTBOX — rendered via portal to escape stacking contexts */}
      {lightboxSrc && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center px-4 md:px-16"
            style={{ zIndex: 9999 }}
            onClick={() => setLightboxSrc(null)}
            data-testid="gallery-lightbox"
          >
            <div className="absolute inset-0 bg-black/90" />
            <button
              onClick={() => setLightboxSrc(null)}
              className="absolute top-4 right-4 z-10 text-white/60 hover:text-white transition-colors"
              data-testid="lightbox-close"
            >
              <X size={28} />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={lightboxSrc}
              alt="Gallery enlarged"
              className="relative z-10 max-h-[85vh] w-auto max-w-full md:max-w-[800px] rounded-lg object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* GET THE DROP MODAL */}
      <AnimatePresence>
        {showDropModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            data-testid="drop-modal"
          >
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/80" onClick={() => setShowDropModal(false)} />

            {/* Modal card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-[480px] overflow-hidden rounded-lg"
              style={{ backgroundColor: '#080010', borderTop: '5px solid #B8FF2E' }}
            >
              {/* Diagonal stripe texture */}
              <div className="absolute inset-0 pointer-events-none opacity-[0.04]" aria-hidden="true" style={{
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, #B8FF2E 10px, #B8FF2E 11px)',
              }} />

              {/* Close button */}
              <button
                onClick={() => setShowDropModal(false)}
                className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors z-20"
                data-testid="drop-modal-close"
              >
                <X size={24} />
              </button>

              <div className="relative z-10 p-8 md:p-10">
                {!dropSubmitted ? (
                  <>
                    <h2 className="font-display text-[36px] md:text-[44px] text-white uppercase leading-[0.95] text-center">
                      You're Not On The List. Yet.
                    </h2>
                    <p className="font-subheading text-[16px] md:text-[18px] uppercase tracking-[2px] text-center mt-3" style={{ color: '#B8FF2E' }}>
                      The Drop Is Invite Only.
                    </p>

                    {/* Body copy with lime left accent bar */}
                    <div className="flex gap-4 mt-6">
                      <div className="flex-shrink-0 w-[3px] rounded-full" style={{ backgroundColor: '#B8FF2E' }} />
                      <p className="font-body text-[14px] leading-relaxed" style={{ color: '#F5F0EE' }}>
                        Drop your number and get access to presale tickets before they go public, exclusive city announcements before anyone else, flash deals and secret crawl locations, and crew-only perks at every event. This does not go to everyone. Only the ones who know.
                      </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleDropSubmit} className="mt-8 space-y-4">
                      <input
                        type="tel"
                        value={dropPhone}
                        onChange={(e) => setDropPhone(e.target.value)}
                        placeholder="your number"
                        className="w-full px-4 py-3.5 font-body text-[15px] text-white rounded-md outline-none transition-colors focus:border-[#B8FF2E]"
                        style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(184,255,46,0.3)' }}
                        data-testid="drop-phone-input"
                        required
                      />
                      <button
                        type="submit"
                        disabled={dropLoading}
                        className="w-full font-display text-[18px] tracking-[2px] uppercase py-4 min-h-[48px] transition-all duration-200 hover:brightness-90 hover:scale-[1.02] rounded-md disabled:opacity-50"
                        style={{ backgroundColor: '#B8FF2E', color: '#080010' }}
                        data-testid="drop-submit-btn"
                      >
                        {dropLoading ? 'JOINING...' : "I'M IN. GET ME THE DROP."}
                      </button>
                    </form>

                    <p className="font-body text-[12px] text-center mt-4" style={{ color: 'rgba(245,240,238,0.35)' }}>
                      No spam. No BS. Just the drop when it matters.
                    </p>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <h2 className="font-display text-[48px] md:text-[56px] text-white uppercase leading-none">
                      You're In.
                    </h2>
                    <p className="font-body text-[16px] mt-4" style={{ color: '#F5F0EE' }}>
                      Watch your phone. The drop is coming.
                    </p>
                    <button
                      onClick={() => setShowDropModal(false)}
                      className="mt-8 w-full font-display text-[18px] tracking-[2px] uppercase py-4 min-h-[48px] transition-all duration-200 hover:brightness-90 hover:scale-[1.02] rounded-md"
                      style={{ backgroundColor: '#B8FF2E', color: '#080010' }}
                      data-testid="drop-got-it-btn"
                    >
                      GOT IT
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomePage;
